import { useNavigate } from "react-router-dom";
import { ArrowRight, Zap, FileText, Presentation, Mic, Users, BarChart3, Shield, Layers, GitBranch, Building2, MousePointer, Film, Eye, Crown, CheckCircle, LayoutGrid } from "lucide-react";

const useCases = [
  { icon: FileText, title: "Cahier des charges", desc: "Générez un CdC professionnel PDF à partir de votre schéma", color: "#6366F1" },
  { icon: BarChart3, title: "Stratégie d'entreprise", desc: "Visualisez votre holding, filiales et flux financiers", color: "#10B981" },
  { icon: Building2, title: "Organigramme", desc: "Structurez votre équipe, rôles et responsabilités", color: "#3B82F6" },
  { icon: GitBranch, title: "Scénario conditionnel", desc: "Modélisez des parcours si/alors avec des règles métier", color: "#F43F5E" },
  { icon: Film, title: "Story Selling & Vidéo", desc: "Structurez vos présentations commerciales et storyboards", color: "#7C3AED" },
  { icon: Layers, title: "Architecture SaaS", desc: "Concevez des apps multi-pages avec landing, admin, client", color: "#0EA5E9" },
];

const features = [
  { icon: Zap, title: "Assistant IA vocal", desc: "Dictez votre projet, l'IA crée le schéma automatiquement" },
  { icon: Layers, title: "Multi-pages & Architecture", desc: "Plusieurs schémas liés avec vue architecture globale" },
  { icon: Presentation, title: "Présentation diapo", desc: "Mode slideshow avec navigation et voix off TTS" },
  { icon: Eye, title: "Review client", desc: "Lien public pour que le client commente chaque étape" },
  { icon: Film, title: "Enregistrement vidéo", desc: "Capturez la présentation en vidéo avec narration" },
  { icon: FileText, title: "Export PDF & PNG", desc: "CdC professionnel A4, schémas cadrés avec signature" },
  { icon: MousePointer, title: "Web Blocks", desc: "Header, Hero, CTA, Section, Footer pour wireframer" },
  { icon: BarChart3, title: "Calculs dynamiques", desc: "Formules entre cartes : somme, moyenne, pourcentage" },
  { icon: Users, title: "17+ templates", desc: "Organigramme, SaaS, process, checklist, parcours client..." },
  { icon: Mic, title: "Pièces jointes", desc: "PDF, images, YouTube + checklist de contenus attendus" },
  { icon: LayoutGrid, title: "8 couleurs de flux", desc: "Connectéurs animés avec couleurs personnalisables" },
  { icon: Shield, title: "Admin complet", desc: "Gestion utilisateurs, licences, paiements, templates" },
];

export default function LandingPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white" data-testid="landing-page">
      {/* Nav */}
      <nav className="fixed top-0 inset-x-0 z-50 bg-white/80 backdrop-blur-xl border-b border-gray-100">
        <div className="max-w-6xl mx-auto flex items-center justify-between px-6 h-14">
          <span className="font-heading text-xl font-bold text-gray-900 tracking-tight">CardFlow</span>
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/login")} className="text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors px-3 py-1.5" data-testid="nav-login">Connexion</button>
            <button onClick={() => navigate("/register")} className="bg-indigo-600 text-white text-xs sm:text-sm font-semibold px-3 sm:px-4 py-2 rounded-lg hover:bg-indigo-700 transition-colors shadow-sm whitespace-nowrap" data-testid="nav-register">Commencer</button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-32 pb-20 px-6 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-indigo-50/50 via-white to-white" />
        <div className="absolute top-20 right-0 w-[500px] h-[500px] bg-indigo-100/30 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-violet-100/20 rounded-full blur-3xl" />
        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-indigo-50 border border-indigo-200 rounded-full px-4 py-1.5 mb-6">
            <Zap size={14} className="text-indigo-600" />
            <span className="text-xs font-semibold text-indigo-700">Propulsé par l'IA — Assistant vocal intégré</span>
          </div>
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-gray-900 tracking-tight leading-[1.1] mb-6">
            Vos idées en
            <span className="bg-gradient-to-r from-indigo-600 to-violet-600 bg-clip-text text-transparent"> schémas intelligents</span>
          </h1>
          <p className="text-lg text-gray-500 max-w-2xl mx-auto mb-10 leading-relaxed">
            Créez des diagrammes d'entreprise en drag & drop. Générez des cahiers des charges, présentez en diapo avec voix off, et collaborez avec vos clients en temps réel.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <button onClick={() => navigate("/register")} className="bg-indigo-600 text-white px-8 py-3.5 rounded-xl text-sm font-bold hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-200 flex items-center gap-2" data-testid="hero-cta">
              Commencer gratuitement <ArrowRight size={16} />
            </button>
            <button onClick={() => document.getÉlémentById("use-cases")?.scrollIntoView({ behavior: "smooth" })} className="text-gray-600 px-6 py-3.5 rounded-xl text-sm font-semibold hover:bg-gray-100 transition-colors border border-gray-200">
              Découvrir les cas d'usage
            </button>
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section id="use-cases" className="py-20 px-6 bg-gray-50/50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">Un outil, des dizaines de cas d'usage</h2>
            <p className="text-gray-500">CardFlow s'adapte à tous vos besoins de modélisation et de présentation.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {useCases.map((uc) => {
              const Icon = uc.icon;
              return (
                <div key={uc.title} className="bg-white rounded-2xl border border-gray-200 p-6 hover:shadow-lg hover:border-gray-300 transition-all group cursor-pointer">
                  <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-4" style={{ backgroundColor: uc.color + "12" }}>
                    <Icon size={20} style={{ color: uc.color }} />
                  </div>
                  <h3 className="font-bold text-gray-900 mb-1.5">{uc.title}</h3>
                  <p className="text-sm text-gray-500 leading-relaxed">{uc.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">Des fonctionnalités qui changent la donne</h2>
            <p className="text-gray-500">Tout ce dont vous avez besoin pour concevoir, présentér et livrer vos projets.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {features.map((f) => {
              const Icon = f.icon;
              return (
                <div key={f.title} className="bg-gray-50 rounded-xl p-5 hover:bg-white hover:shadow-md hover:border-gray-200 border border-transparent transition-all">
                  <Icon size={18} className="text-indigo-600 mb-3" />
                  <h4 className="font-semibold text-sm text-gray-900 mb-1">{f.title}</h4>
                  <p className="text-xs text-gray-500 leading-relaxed">{f.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-20 px-6 bg-gray-50/50" id="pricing">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">Des offres adaptées à vos besoins</h2>
            <p className="text-gray-500">Commencez gratuitement, évoluez à votre rythme.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {/* Free */}
            <div className="bg-white rounded-2xl border border-gray-200 p-7">
              <h3 className="font-bold text-lg text-gray-900 mb-1">Gratuit</h3>
              <p className="text-xs text-gray-400 mb-5">Pour découvrir</p>
              <div className="flex items-baseline gap-1 mb-5">
                <span className="text-4xl font-bold text-gray-900">0</span>
                <span className="text-gray-400 text-sm">EUR</span>
              </div>
              <ul className="space-y-2.5 mb-7">
                {["1 diagramme", "9 types de noeuds", "Export PNG", "Templates de base"].map(f => (
                  <li key={f} className="flex items-center gap-2 text-sm text-gray-600">
                    <CheckCircle size={13} className="text-gray-300 shrink-0" /> {f}
                  </li>
                ))}
              </ul>
              <button onClick={() => navigate("/register")} className="w-full py-2.5 rounded-xl text-sm font-semibold border border-gray-200 text-gray-700 hover:bg-gray-50 transition-colors">
                Commencer
              </button>
            </div>
            {/* Pro */}
            <div className="bg-white rounded-2xl border-2 border-indigo-500 p-7 relative shadow-lg">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-indigo-600 text-white text-[10px] font-bold px-3 py-1 rounded-full uppercase tracking-wider">Populaire</div>
              <h3 className="font-bold text-lg text-gray-900 mb-1">Pro</h3>
              <p className="text-xs text-gray-400 mb-5">Pour les indépendants</p>
              <div className="flex items-baseline gap-1 mb-5">
                <span className="text-4xl font-bold text-gray-900">19</span>
                <span className="text-gray-400 text-sm">EUR / mois</span>
              </div>
              <ul className="space-y-2.5 mb-7">
                {[
                  { text: "10 diagrammes", included: true },
                  { text: "Multi-pages & Architecture", included: true },
                  { text: "15 types de nœuds + Web Blocs", included: true },
                  { text: "Export PNG", included: true },
                  { text: "Tous les templates", included: true },
                  { text: "Pièces jointes & contenus", included: true },
                  { text: "Assistant IA vocal", included: false, label: "Entreprise" },
                  { text: "Export PDF & CdC", included: false, label: "Entreprise" },
                  { text: "Review client & Portail", included: false, label: "Entreprise" },
                  { text: "Voix off TTS & Video", included: false, label: "Entreprise" },
                ].map(f => (
                  <li key={f.text} className="flex items-center gap-2 text-sm">
                    {f.included ? (
                      <><CheckCircle size={13} className="text-indigo-500 shrink-0" /> <span className="text-gray-700">{f.text}</span></>
                    ) : (
                      <><CheckCircle size={13} className="text-gray-200 shrink-0" /> <span className="text-gray-400">{f.text}</span> <span className="text-[8px] font-bold bg-violet-100 text-violet-600 px-1.5 py-0.5 rounded-full ml-auto shrink-0">{f.label}</span></>
                    )}
                  </li>
                ))}
              </ul>
              <button onClick={() => navigate("/register")} className="w-full py-2.5 rounded-xl text-sm font-bold bg-indigo-600 text-white hover:bg-indigo-700 transition-colors shadow-sm">
                Essayer Pro
              </button>
            </div>
            {/* Entreprise */}
            <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-7 text-white relative overflow-hidden">
              <div className="absolute top-0 right-0 w-40 h-40 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
              <div className="relative z-10">
                <div className="flex items-center gap-2 mb-1">
                  <Crown size={16} className="text-amber-400" />
                  <h3 className="font-bold text-lg">Entreprise</h3>
                </div>
                <p className="text-xs text-gray-400 mb-5">Tout inclus, sans limites</p>
                <div className="flex items-baseline gap-1 mb-5">
                  <span className="text-4xl font-bold">49</span>
                  <span className="text-gray-400 text-sm">EUR / mois</span>
                </div>
                <ul className="space-y-2.5 mb-7">
                  {["Diagrammes illimités", "Tout le plan Pro inclus", "Assistant IA vocal", "Export PDF & Cahier des charges", "Review client & Portail client", "Présentation avec voix off TTS", "Enregistrement vidéo", "Gestion des accès & clés API", "Support prioritaire"].map(f => (
                    <li key={f} className="flex items-center gap-2 text-sm text-gray-300">
                      <CheckCircle size={13} className="text-amber-400 shrink-0" /> {f}
                    </li>
                  ))}
                </ul>
                <button onClick={() => navigate("/register")} className="w-full py-2.5 rounded-xl text-sm font-bold bg-white text-gray-900 hover:bg-gray-100 transition-colors shadow-lg">
                  Passer Entreprise
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Prêt à transformer vos idées en schémas ?</h2>
          <p className="text-gray-500 mb-8">Rejoignez les professionnels qui utilisent CardFlow pour concevoir, présenter et livrer leurs projets.</p>
          <button onClick={() => navigate("/register")} className="bg-gray-900 text-white px-8 py-3.5 rounded-xl text-sm font-bold hover:bg-gray-800 transition-colors shadow-lg flex items-center gap-2 mx-auto" data-testid="final-cta">
            Créer mon premier schéma <ArrowRight size={16} />
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-gray-100 py-8 px-6">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <span className="text-sm font-semibold text-gray-900">CardFlow</span>
          <span className="text-xs text-gray-400">Construit avec passion — {new Date().getFullYear()}</span>
        </div>
      </footer>
    </div>
  );
}
